<script setup lang="ts">
const props = defineProps<{
  curriculumName?: string;
  licenseStatusLabel?: string;
  shouldConfirmCurriculumChange?: boolean;
  isLicenseExpired?: boolean;
  transferLicenseAssignmentId?: number;
  onClose?: () => void | Promise<void>;
}>();

const runtimeConfig = useRuntimeConfig();
const areLicenseTransfersEnabled = computed(
  () => runtimeConfig.public.companionFeatures?.licenseTransfersEnabled === true
);
const companionStore = useCompanion();
const { selectedProfile } = storeToRefs(companionStore);
const levelsStore = useLevels();
const { allLevels } = storeToRefs(levelsStore);
const activeView = ref<"settings" | "curriculum" | "transfer">("settings");
const transferEmail = ref("");
const corporateTransferId = ref("");
const transferStudents = ref<Array<{ id: string; name: string }>>([]);
const selectedTransferStudentId = ref("");
const isTransferring = ref(false);
const transferError = ref("");
const profileName = ref(selectedProfile.value?.name ?? "");
const isRenamingProfile = ref(false);
const isArchivingProfile = ref(false);

watch(
  () => selectedProfile.value?.name,
  (name) => {
    if (!isRenamingProfile.value) profileName.value = name ?? "";
  }
);

const renameProfile = async () => {
  const trimmedName = profileName.value.trim();
  if (!trimmedName || trimmedName === selectedProfile.value?.name) return;

  isRenamingProfile.value = true;
  try {
    const renamed = await companionStore.renameProfile(trimmedName);
    if (renamed) {
      profileName.value = trimmedName;
      useBaseToast("Profile renamed.", "success");
    }
  } catch (error) {
    useBaseToast("Unable to rename the profile.", "error");
  } finally {
    isRenamingProfile.value = false;
  }
};

const archiveProfile = async () => {
  const profile = selectedProfile.value;
  if (!profile) return;

  const confirmed = await useBaseConfirm(
    "Delete Profile",
    `Delete ${profile.name}? The profile will be removed, but its papers will be kept.`,
    false,
    "Delete Profile",
    "Cancel"
  );
  if (!confirmed) return;

  isArchivingProfile.value = true;
  try {
    const archived = await companionStore.archiveProfile();
    if (!archived) return;

    useBaseToast("Profile deleted. Its papers were kept.", "success");
    await props.onClose?.();
  } catch (error) {
    useBaseToast("Unable to delete the profile.", "error");
  } finally {
    isArchivingProfile.value = false;
  }
};

const selectedCurriculumName = computed(() => {
  const curriculum = allLevels.value.find((level) => {
    return level.id === selectedProfile.value?.curriculumLevelId;
  });

  return curriculum?.name ?? props.curriculumName;
});

const changeCurriculum = async () => {
  if (props.shouldConfirmCurriculumChange) {
    const response = await useBaseConfirm(
      "Are you sure?",
      "Are you sure you want to change your curriculum?",
      false,
      "Change Curriculum",
      "Cancel"
    );

    if (!response) return;
  }

  activeView.value = "curriculum";
};

const returnToSettings = () => {
  activeView.value = "settings";
  transferError.value = "";
};

const beginTransfer = () => {
  transferEmail.value = "";
  corporateTransferId.value = "";
  transferStudents.value = [];
  selectedTransferStudentId.value = "";
  transferError.value = "";
  activeView.value = "transfer";
};

const transferLicense = async () => {
  const profile = selectedProfile.value;
  const email = transferEmail.value.trim();
  if (!profile?.id || (!corporateTransferId.value && !email)) {
    transferError.value = "Enter the email used to log into IEW Gradebook.";
    return;
  }

  if (!corporateTransferId.value) {
    const confirmed = await useBaseConfirm(
      "Transfer",
      `Transfer the IEW Writing Companion purchase from ${profile.name} to the IEW Gradebook Student Portal email ${email}? This is a one-way transfer and cannot be undone.`,
      false,
      "Transfer",
      "Cancel"
    );
    if (!confirmed) return;
  }

  isTransferring.value = true;
  transferError.value = "";
  try {
    const response = await $fetch<{
      status:
        | "selectionRequired"
        | "migrating"
        | "activationPending"
        | "completed";
      transferId: string;
      students?: Array<{ id: string; name: string }>;
    }>(
      "/api/companion/licenses/transfer",
      {
        method: "POST",
        headers: await useApiHeaders(),
        body: {
          ...(corporateTransferId.value
            ? {
                transferId: corporateTransferId.value,
                studentId: selectedTransferStudentId.value,
              }
            : {
                profileId: profile.id,
                email,
                licenseAssignmentId: props.transferLicenseAssignmentId,
              }),
        },
      }
    );
    corporateTransferId.value = response.transferId;
    if (response.status === "selectionRequired") {
      transferStudents.value = response.students || [];
      selectedTransferStudentId.value =
        transferStudents.value.length === 1 ? transferStudents.value[0].id : "";
      return;
    }
    if (response.status === "completed") {
      useBaseToast("License transferred to the Gradebook student.", "success");
      await props.onClose?.();
      return;
    }
    transferError.value =
      response.status === "activationPending"
        ? "The IEW order was transferred. License activation is still being completed; no additional purchase is needed."
        : "IEW is still confirming the transfer. Select Continue to check again.";
  } catch (error: any) {
    transferError.value =
      error?.data?.message ||
      error?.message ||
      "Unable to transfer the license.";
  } finally {
    isTransferring.value = false;
  }
};
</script>

<template>
  <div
    class="flex w-[min(92vw,760px)] flex-col bg-white rounded-xl overflow-hidden"
  >
    <template v-if="activeView === 'settings'">
      <div class="space-y-4 px-6 py-6 bg-gray-50">
        <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <label
            class="mb-2 block text-sm font-semibold text-gray-700"
            for="profile-name"
          >
            Profile name
          </label>
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <BaseTextInput
              id="profile-name"
              v-model="profileName"
              class="w-full flex-1"
              :disabled="isRenamingProfile || isArchivingProfile"
              @keyup.enter="renameProfile"
            />
            <BaseButton
              size="sm"
              :disabled="
                !profileName.trim() ||
                profileName.trim() === selectedProfile?.name ||
                isArchivingProfile
              "
              :show-spinner="isRenamingProfile"
              @click="renameProfile"
            >
              Rename
            </BaseButton>
          </div>
        </div>

        <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <div class="truncate text-lg font-bold text-gray-900">
                {{ selectedCurriculumName ?? "No curriculum selected" }}
              </div>
              <div
                v-if="licenseStatusLabel"
                class="mt-1.5 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset"
                :class="[
                  isLicenseExpired
                    ? 'bg-danger-default/10 text-danger-default ring-danger-default/20'
                    : 'bg-primary-default/10 text-primary-default ring-primary-default/20',
                ]"
              >
                {{ licenseStatusLabel }}
              </div>
            </div>
            <BaseOutlinedButton
              size="sm"
              @click="changeCurriculum"
            >
              Change Curriculum
            </BaseOutlinedButton>
          </div>
        </div>

        <div
          v-if="areLicenseTransfersEnabled && transferLicenseAssignmentId"
          class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
        >
          <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div class="min-w-0">
              <div class="font-bold text-gray-900">Gradebook Student Account</div>
              <p class="mt-1 text-sm text-gray-600">
                Move this license to a student who signs into the IEW Gradebook
                Student Portal.
              </p>
            </div>
            <BaseOutlinedButton size="sm" @click="beginTransfer">
              Transfer
            </BaseOutlinedButton>
          </div>
        </div>

        <div
          class="rounded-xl border border-danger-default/30 bg-white p-5 shadow-sm"
        >
          <div
            class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="min-w-0">
              <div class="font-bold text-gray-900">Delete profile</div>
              <p class="mt-1 text-sm text-gray-600">
                Remove this profile from your account. Papers associated with
                it will be kept.
              </p>
            </div>
            <BaseButton
              size="sm"
              color="danger"
              :show-spinner="isArchivingProfile"
              :disabled="isRenamingProfile"
              @click="archiveProfile"
            >
              Delete Profile
            </BaseButton>
          </div>
        </div>
      </div>
    </template>

    <CompanionCreateSubmissionCurriculumPicker
      v-else-if="activeView === 'curriculum'"
      title="Change Curriculum"
      description="Choose the curriculum for this companion profile."
      cancel-label="Back"
      :on-cancel="returnToSettings"
      :on-saved="returnToSettings"
    />

    <template v-else>
      <div class="space-y-5 bg-gray-50 px-6 py-6">
        <div class="mb-1">
          <h2 class="text-xl font-bold text-gray-900">Transfer Your IEW Writing Companion Purchase</h2>
        </div>

        <div class="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p class="font-semibold text-amber-950">This is a one-way transfer.</p>
          <p class="mt-1 text-sm text-amber-900">
            Your IEW Writing Companion purchase will be transferred from {{ selectedProfile?.name }} to the IEW Gradebook Student Portal email you provide below.
          </p>
        </div>

        <label v-if="!corporateTransferId" class="block">
          <span class="mb-2 block text-sm font-semibold text-gray-800">
            IEW Gradebook Student Portal email
          </span>
          <BaseTextInput
            v-model="transferEmail"
            class="w-full"
            type="email"
            :disabled="isTransferring"
            @keyup.enter="transferLicense"
          />
        </label>

        <div v-else-if="transferStudents.length" class="space-y-3">
          <p class="text-sm font-semibold text-gray-800">
            Choose the Gradebook student who should receive this license.
          </p>
          <button
            v-for="student in transferStudents"
            :key="student.id"
            type="button"
            class="block w-full rounded-xl border-2 p-4 text-left transition"
            :class="
              selectedTransferStudentId === student.id
                ? 'border-primary bg-primary/5 ring-2 ring-primary/15'
                : 'border-gray-200 bg-white hover:border-primary/40 hover:bg-primary/5'
            "
            :disabled="isTransferring"
            @click="selectedTransferStudentId = student.id"
          >
            <span class="font-bold text-gray-950">{{ student.name }}</span>
          </button>
        </div>

        <p
          v-if="transferError"
          class="rounded-lg bg-danger-default/10 px-4 py-3 text-sm font-medium text-danger-default"
        >
          {{ transferError }}
        </p>

        <div class="flex justify-end gap-3">
          <BaseOutlinedButton
            color="medium"
            :disabled="isTransferring"
            @click="returnToSettings"
          >
            Back
          </BaseOutlinedButton>
          <BaseButton
            :show-spinner="isTransferring"
            :disabled="
              corporateTransferId
                ? transferStudents.length > 0 && !selectedTransferStudentId
                : !transferEmail.trim()
            "
            @click="transferLicense"
          >
            Continue
          </BaseButton>
        </div>
      </div>
    </template>
  </div>
</template>
